import twitterText from 'twitter-text'

import { AttachmentTypes } from '../AppConstants'
import type { Post } from '../entities/Draft/Draft'

type BuildThreadedPostReplacementArgs = {
  activePost: Post
  segments: readonly string[]
  createPost: (text: string) => Post
  getCharacterCount: (text: string) => number
  getCanonicalUrl: (url: string) => string
}

function partitionUrlsBySegment(
  segments: readonly string[],
  urls: readonly string[],
  getComparableUrl: (url: string) => string = (url) => url,
): string[][] {
  const remainingUrls = [...urls]

  // Each tracked URL belongs to at most one replacement post. Canonicalized
  // comparison keeps long-form URL history with text containing a short link.
  return segments.map((segment) =>
    twitterText.extractUrls(segment).flatMap((url) => {
      const comparableUrl = getComparableUrl(url)
      const urlIndex = remainingUrls.findIndex(
        (remainingUrl) => getComparableUrl(remainingUrl) === comparableUrl,
      )
      if (urlIndex === -1) return []

      return remainingUrls.splice(urlIndex, 1)
    }),
  )
}

export function buildThreadedPostReplacement({
  activePost,
  segments,
  createPost,
  getCharacterCount,
  getCanonicalUrl,
}: BuildThreadedPostReplacementArgs): Post[] {
  const urlsBySegment = partitionUrlsBySegment(segments, activePost.urls)
  const unshortenedUrlsBySegment = partitionUrlsBySegment(
    segments,
    activePost.unshortenedUrls,
    getCanonicalUrl,
  )

  const replacement = segments.map((text, index) => {
    const urls = urlsBySegment[index] ?? []
    const unshortenedUrls = unshortenedUrlsBySegment[index] ?? []
    if (index !== 0) {
      const post = createPost(text)
      post.urls = urls
      post.unshortenedUrls = unshortenedUrls
      return post
    }

    return {
      text,
      get characterCount(): number {
        return getCharacterCount(this.text)
      },
      images: [...activePost.images],
      video: activePost.video,
      videos: [...activePost.videos],
      gif: activePost.gif,
      link: activePost.link,
      retweet: activePost.retweet,
      urls,
      unshortenedUrls,
      enabledAttachmentType: activePost.enabledAttachmentType,
    }
  })

  const activeLink = activePost.link
  const canonicalActiveLinkUrl = activeLink
    ? getCanonicalUrl(activeLink.url)
    : null
  const linkSegmentIndex = canonicalActiveLinkUrl
    ? segments.findIndex((segment) =>
        twitterText
          .extractUrls(segment)
          .some((url) => getCanonicalUrl(url) === canonicalActiveLinkUrl),
      )
    : -1
  const firstPost = replacement[0]
  const linkPost = replacement[linkSegmentIndex]

  if (activeLink && firstPost && linkPost && linkSegmentIndex > 0) {
    firstPost.link = null
    linkPost.link = activeLink

    if (activePost.enabledAttachmentType === AttachmentTypes.LINK) {
      firstPost.enabledAttachmentType = null
      linkPost.enabledAttachmentType = AttachmentTypes.LINK
    }
  }

  return replacement
}
